/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { Announced } from '@fluentui/react/lib/Announced';
import { IButtonProps, IButtonStyles, IconButton } from '@fluentui/react/lib/Button';
import { Icon, IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { AnimationStyles } from '@fluentui/react/lib/Styling';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';

export type CarouselSlide = {
    key: string;
    header: JSX.Element;
    content: JSX.Element;
    contentFooter?: JSX.Element;
    announcedMessage: string;
};

export type CarouselProps = {
    /**
     * Whether the slides should be repeatable.
     */
    infiniteLoop?: boolean;

    /**
     * The current active slide's key.
     */
    activeSlideKey: string;

    /**
     * slides to display.
     */
    slides: CarouselSlide[];

    /**
     * A callback whenever the slide change.
     */
    onSlideChange: (newSlideKey: string) => void;

    /**
     * Whether the carousel contains one slide or not.
     */
    isOneSlide?: boolean;
};

const messages = defineMessages({
    nextIconButtonArialLabel: {
        id: 'languageStudio.carousel.nextIconButtonArialLabel',
        defaultMessage: 'next'
    },
    previousIconButtonAriaLabel: {
        id: 'languageStudio.carousel.previousIconButtonAriaLabel',
        defaultMessage: 'previous'
    }
});

const addAnimation = (element: JSX.Element, animationDirection: 'left' | 'right', key: string) =>
    React.cloneElement(element, {
        key: `${key}`,
        ...element.props,
        style: animationDirection === 'left' ? AnimationStyles.slideRightIn40 : AnimationStyles.slideLeftIn40
    });

const theme = getScopedTheme('Carousel');
const stackStyler = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>();

const carouselContainerStyles = stackStyler({ root: { margin: '0 20px' } });
const carouselContentStyles = stackStyler({ root: { backgroundColor: theme.backgroundColor } });
const iconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({ root: { color: theme.backgroundColor, fontSize: '10px' } });
const iconButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    icon: { fontSize: '21px' },
    rootDisabled: { backgroundColor: 'transparent !important' }
});

export const Carousel = (props: CarouselProps) => {
    const { activeSlideKey, infiniteLoop = true, slides, onSlideChange, isOneSlide = false } = props;

    const { localize } = useLocalization();

    const [direction, setDirection] = React.useState<'right' | 'left'>('left');

    const slide = slides.find(s => s.key === activeSlideKey);

    const handleSlide = (newDirection: 'left' | 'right') => {
        setDirection(newDirection);

        const slideIndex = slides.findIndex(s => s.key === activeSlideKey);
        let nextSlideIndex = -1;

        switch (newDirection) {
            case 'left':
                nextSlideIndex = slideIndex === 0 ? slides.length - 1 : slideIndex - 1;
                break;
            case 'right':
                nextSlideIndex = slideIndex === slides.length - 1 ? 0 : slideIndex + 1;
                break;
            default:
        }

        onSlideChange(slides[nextSlideIndex].key);
    };

    return (
        <Stack styles={carouselContainerStyles}>
            <Stack grow horizontal>
                {slide.header}
            </Stack>

            <Stack grow horizontal styles={carouselContentStyles} tokens={{ padding: '0 12px' }} horizontalAlign="space-between">
                {!isOneSlide && (
                    <Stack verticalAlign="center">
                        <IconButton
                            styles={iconButtonStyles}
                            onClick={() => handleSlide('left')}
                            iconProps={{ iconName: 'ChevronLeft' }}
                            disabled={!infiniteLoop && activeSlideKey === slides[0].key}
                            ariaLabel={localize(messages.previousIconButtonAriaLabel)}
                        />
                    </Stack>
                )}

                <Stack>
                    {addAnimation(slide.content, direction, `slideContent_${slide.key}`)}
                    {slide.contentFooter && addAnimation(slide.contentFooter, direction, `slideContentFooter_${slide.key}`)}
                    <Announced message={slide.announcedMessage} aria-live="assertive" />
                </Stack>

                {!isOneSlide && (
                    <Stack verticalAlign="center">
                        <IconButton
                            styles={iconButtonStyles}
                            onClick={() => handleSlide('right')}
                            data-automation-id="carouselRightButton"
                            iconProps={{ iconName: 'ChevronRight' }}
                            disabled={!infiniteLoop && activeSlideKey === slides[slides.length - 1].key}
                            ariaLabel={localize(messages.nextIconButtonArialLabel)}
                        />
                    </Stack>
                )}
            </Stack>

            {!isOneSlide && (
                <Stack horizontal horizontalAlign="center" tokens={{ childrenGap: 16, padding: '20px' }}>
                    {slides.map(s => (
                        <Icon key={s.key} styles={iconStyles} iconName={activeSlideKey === s.key ? 'CircleFill' : 'CircleRing'} />
                    ))}
                </Stack>
            )}
        </Stack>
    );
};
